import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API_BASE_URL, WS_BASE_URL } from "../services/apiConfig";

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('casapizza_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SupportDashboard() {

  const [agentName, setAgentName] = useState(
    localStorage.getItem("casapizza_agent_name") || ""
  );
  const [agentInput, setAgentInput] = useState("");

  // ─── Estado de chats con paginación ──────────────────────────────────────
  const [chats, setChats] = useState([]);
  const [chatPage, setChatPage] = useState(1);
  // { total, page, limit, pages, has_next, has_prev }
  const [chatPagination, setChatPagination] = useState(null);
  // "default" = excluye CLOSED | "WAITING_AGENT" | "all"
  const [statusFilter, setStatusFilter] = useState("default");

  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [customerHistory, setCustomerHistory] = useState(null);
  const messagesEndRef = useRef(null);
  const [newMessage, setNewMessage] = useState("");
  const wsRef = useRef(null);
  const selectedChatIdRef = useRef(null);

  // ─── Búsqueda de clientes (C2b) ───────────────────────────────────────────
  const [rightTab, setRightTab] = useState("chat"); // "chat" | "search"
  const [searchPhone, setSearchPhone] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // ─── Carga de chats (única función — consolida loadChats + fetchChats) ────
  //
  // Cambios vs. versión anterior:
  //   - Acepta page y filter como parámetros (sin depender solo del estado)
  //     para que WebSocket y eventos de paginación usen valores actualizados.
  //   - Consume el nuevo formato { chats:[...], pagination:{...} }.
  //   - Elimina la función duplicada fetchChats() que usaba axios.
  const loadChats = async (page = chatPage, filter = statusFilter) => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter !== "default") params.append("status", filter);

      const res = await fetch(`${API_BASE_URL}/support/chats?${params}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();

      setChats(data.chats);
      setChatPagination(data.pagination);

      if (!selectedChat && data.chats.length > 0) {
        setSelectedChat(data.chats[0]);
      }
    } catch (_) {
      // backend temporalmente no disponible — ignorar silenciosamente
    }
  };

  const loadMessages = async (sessionId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/support/chat/${sessionId}/messages`,
        { headers: getAuthHeaders() }
      );
      setMessages(response.data);
    } catch (error) {
      console.error("Error cargando mensajes:", error);
    }
  };

  const loadCustomerHistory = async (sessionId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/support/chat/${sessionId}/customer-history`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      setCustomerHistory(data);
    } catch (_) {
      setCustomerHistory(null);
    }
  };

  // Scroll al último mensaje cuando cambia la lista
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ─── WebSocket de soporte ─────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    let wsInstance = null;
    let pingInterval = null;
    let reconnectTimer = null;

    function clearTimers() {
      if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    }

    function connect() {
      if (!active) return;
      const token = sessionStorage.getItem('casapizza_token');
      const ws = new WebSocket(`${WS_BASE_URL}/ws/support?token=${token}`);
      wsInstance = ws;
      wsRef.current = ws;

      ws.onopen = () => {
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 5000);
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        // Al recibir evento de chat actualizado, volver a página 1
        // para que los chats urgentes aparezcan arriba
        if (data.type === 'CHAT_UPDATED') loadChats(1, statusFilter);
        if (data.type === 'NEW_MESSAGE' && data.chat_id === selectedChatIdRef.current) {
          loadMessages(data.chat_id);
        }
      };

      ws.onclose = () => {
        clearTimers();
        wsRef.current = null;
        if (active) reconnectTimer = setTimeout(connect, 3000);
      };

      ws.onerror = () => { ws.close(); };
    }

    const initialTimeout = setTimeout(connect, 0);

    return () => {
      active = false;
      clearTimeout(initialTimeout);
      clearTimers();
      if (wsInstance) { wsInstance.close(); wsRef.current = null; }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Polling de respaldo (30s) ────────────────────────────────────────────
  useEffect(() => {
    loadChats();
    const interval = setInterval(() => loadChats(), 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Acciones de chat ─────────────────────────────────────────────────────
  const takeChat = async (id) => {
    await fetch(`${API_BASE_URL}/support/chat/${id}/take?agent=${encodeURIComponent(agentName)}`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
    loadChats();
  };

  const releaseChat = async (id) => {
    await fetch(`${API_BASE_URL}/support/chat/${id}/release`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
    loadChats();
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    try {
      await axios.post(
        `${API_BASE_URL}/support/chat/${selectedChat.id}/send`,
        { message: newMessage, agent: agentName },
        { headers: getAuthHeaders() }
      );
      setNewMessage("");
      loadMessages(selectedChat.id);
      loadChats();
    } catch (error) {
      console.error(error);
    }
  };

  const searchCustomer = async () => {
    const q = searchPhone.trim();
    if (!q) return;
    setSearchLoading(true);
    setSearchResult(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/support/customers/search?phone=${encodeURIComponent(q)}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      setSearchResult(data);
    } catch (_) {
      setSearchResult({ found: false, customer_phone: q, orders: [] });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSetAgent = () => {
    const name = agentInput.trim();
    if (!name) return;
    localStorage.setItem("casapizza_agent_name", name);
    setAgentName(name);
  };

  const getStatusColor = (chat) => {
    if (chat.status === "WAITING_AGENT") return "#f59e0b";
    if (chat.mode === "HUMAN") return "#22c55e";
    return "#64748b";
  };

  // ─── Pantalla de registro de agente ──────────────────────────────────────
  if (!agentName) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", height: "100vh", fontFamily: "Arial",
                    background: "#f8fafc" }}>
        <h2 style={{ marginBottom: "24px" }}>💬 Panel de Soporte</h2>
        <p style={{ color: "#555", marginBottom: "16px" }}>Ingresa tu nombre para continuar</p>
        <input
          value={agentInput}
          onChange={(e) => setAgentInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSetAgent()}
          placeholder="Tu nombre..."
          autoFocus
          style={{ padding: "12px 16px", borderRadius: "8px", border: "1px solid #ccc",
                   fontSize: "16px", width: "280px", marginBottom: "12px", outline: "none" }}
        />
        <button
          onClick={handleSetAgent}
          style={{ padding: "12px 32px", background: "#25D366", color: "white",
                   border: "none", borderRadius: "8px", fontSize: "16px",
                   cursor: "pointer", fontWeight: "bold" }}
        >
          Entrar al soporte
        </button>
      </div>
    );
  }

  // ─── Tabs de filtro ───────────────────────────────────────────────────────
  const filterTabs = [
    { label: "Activos", value: "default" },
    { label: "⏳ Pendientes", value: "WAITING_AGENT" },
    { label: "Todos", value: "all" },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "Arial" }}>

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <div style={{ width: "30%", borderRight: "1px solid #ddd",
                    display: "flex", flexDirection: "column", background: "#f8fafc" }}>

        {/* Header del sidebar */}
        <div style={{ padding: "16px", borderBottom: "1px solid #ddd", flexShrink: 0 }}>
          <div style={{ fontSize: "22px", fontWeight: "bold" }}>💬 Soporte WhatsApp</div>
          <div style={{ fontSize: "13px", color: "#555", marginTop: "6px" }}>
            Agente: <strong>{agentName}</strong>
            <button
              onClick={() => {
                localStorage.removeItem("casapizza_agent_name");
                setAgentName("");
                setAgentInput("");
              }}
              style={{ marginLeft: 8, fontSize: "11px", padding: "2px 8px",
                       cursor: "pointer", border: "1px solid #ccc",
                       borderRadius: "4px", background: "#fff" }}
            >
              Cambiar
            </button>
          </div>
          {/* Contador de conversaciones */}
          {chatPagination && (
            <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
              {chatPagination.total} conversación{chatPagination.total !== 1 ? "es" : ""}
            </div>
          )}
        </div>

        {/* Tabs de filtro */}
        <div style={{ display: "flex", gap: "6px", padding: "8px 12px",
                      borderBottom: "1px solid #ddd", flexShrink: 0, flexWrap: "wrap" }}>
          {filterTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setStatusFilter(tab.value);
                setChatPage(1);
                loadChats(1, tab.value);
              }}
              style={{
                padding: "4px 10px", fontSize: "12px", borderRadius: "12px",
                border: "1px solid #ddd", cursor: "pointer",
                background: statusFilter === tab.value ? "#25D366" : "#fff",
                color: statusFilter === tab.value ? "white" : "#333",
                fontWeight: statusFilter === tab.value ? "bold" : "normal",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Lista de chats */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {chats.length === 0 ? (
            <div style={{ padding: "20px", color: "#888", textAlign: "center",
                          fontSize: "13px" }}>
              Sin conversaciones activas
            </div>
          ) : (
            chats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => {
                  setSelectedChat(chat);
                  selectedChatIdRef.current = chat.id;
                  loadMessages(chat.id);
                  loadCustomerHistory(chat.id);
                }}
                style={{
                  padding: "14px",
                  cursor: "pointer",
                  borderBottom: "1px solid #eee",
                  background: selectedChat?.id === chat.id ? "#e2e8f0" : "transparent",
                }}
              >
                <div style={{ fontWeight: "bold" }}>{chat.phone}</div>
                <div style={{ fontSize: "13px", color: "#666", marginTop: "4px",
                              overflow: "hidden", textOverflow: "ellipsis",
                              whiteSpace: "nowrap" }}>
                  {chat.last_message || "Sin mensajes"}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "center", marginTop: "6px" }}>
                  <div style={{ fontSize: "12px", color: getStatusColor(chat),
                                fontWeight: "bold" }}>
                    {chat.mode} / {chat.status}
                  </div>
                  {/* updated_at ahora viene del backend (fix B7) */}
                  <div style={{ fontSize: "11px", color: "#999" }}>
                    {chat.updated_at
                      ? new Date(chat.updated_at).toLocaleTimeString([], {
                          hour: "2-digit", minute: "2-digit",
                        })
                      : ""}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Controles de paginación */}
        {chatPagination && chatPagination.pages > 1 && (
          <div style={{ padding: "12px", borderTop: "1px solid #ddd", flexShrink: 0,
                        display: "flex", justifyContent: "space-between",
                        alignItems: "center", background: "#f0f2f5" }}>
            <button
              onClick={() => {
                const newPage = chatPage - 1;
                setChatPage(newPage);
                loadChats(newPage);
              }}
              disabled={!chatPagination.has_prev}
              style={{ padding: "6px 12px", fontSize: "12px", borderRadius: "6px",
                       border: "1px solid #ddd", cursor: chatPagination.has_prev ? "pointer" : "default",
                       background: chatPagination.has_prev ? "#fff" : "#f5f5f5",
                       color: chatPagination.has_prev ? "#333" : "#aaa" }}
            >
              ← Anterior
            </button>
            <span style={{ fontSize: "12px", color: "#555" }}>
              {chatPage} / {chatPagination.pages}
            </span>
            <button
              onClick={() => {
                const newPage = chatPage + 1;
                setChatPage(newPage);
                loadChats(newPage);
              }}
              disabled={!chatPagination.has_next}
              style={{ padding: "6px 12px", fontSize: "12px", borderRadius: "6px",
                       border: "1px solid #ddd", cursor: chatPagination.has_next ? "pointer" : "default",
                       background: chatPagination.has_next ? "#fff" : "#f5f5f5",
                       color: chatPagination.has_next ? "#333" : "#aaa" }}
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {/* ── Panel central: conversación ──────────────────────────────────── */}
      <div style={{ width: "45%", display: "flex", flexDirection: "column",
                    height: "100vh" }}>
        <div style={{ padding: "16px", borderBottom: "1px solid #ddd",
                      fontWeight: "bold", fontSize: "20px", flexShrink: 0 }}>
          {selectedChat ? selectedChat.phone : "Selecciona un chat"}
        </div>

        <div style={{ flex: 1, padding: "20px", background: "#efeae2",
                      overflowY: "auto", display: "flex", flexDirection: "column",
                      gap: "10px" }}>
          {selectedChat ? (
            messages.length > 0 ? (
              messages.map((msg) => {
                const isUser = msg.sender === "USER";
                return (
                  <div key={msg.id} style={{ display: "flex",
                                             justifyContent: isUser ? "flex-start" : "flex-end" }}>
                    <div style={{
                      background: isUser ? "#ffffff" : "#005c4b",
                      border: isUser ? "1px solid #e5e7eb" : "none",
                      color: isUser ? "#111" : "white",
                      padding: "12px 14px", borderRadius: "16px", maxWidth: "70%",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                      <div style={{ fontSize: "11px", opacity: 0.7,
                                    marginBottom: "6px", fontWeight: "bold" }}>
                        {msg.sender}
                      </div>
                      <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
                        {msg.message}
                      </div>
                      <div style={{ marginTop: "6px", fontSize: "10px",
                                    opacity: 0.6, textAlign: "right" }}>
                        {new Date(msg.created_at).toLocaleTimeString([], {
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ color: "#666" }}>Sin mensajes aún</div>
            )
          ) : (
            <div style={{ color: "#666" }}>Selecciona una conversación</div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input de mensaje */}
        <div style={{ padding: "12px", borderTop: "1px solid #ddd",
                      display: "flex", gap: "10px", flexShrink: 0,
                      background: "#f0f2f5" }}>
          <input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }}
            placeholder="Escribir respuesta..."
            style={{ flex: 1, padding: "12px", borderRadius: "20px",
                     border: "1px solid #ccc", outline: "none" }}
          />
          <button
            onClick={sendMessage}
            style={{ padding: "12px 20px", border: "none", borderRadius: "20px",
                     background: "#25D366", color: "white", cursor: "pointer",
                     fontWeight: "bold" }}
          >
            Enviar
          </button>
        </div>
      </div>

      {/* ── Panel derecho: info del cliente + búsqueda ─────────────────── */}
      <div style={{ width: "25%", borderLeft: "1px solid #ddd",
                    background: "#fafafa", overflowY: "auto",
                    display: "flex", flexDirection: "column" }}>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #ddd", flexShrink: 0 }}>
          {[{ key: "chat", label: "Chat" }, { key: "search", label: "Buscar cliente" }].map((t) => (
            <button
              key={t.key}
              onClick={() => setRightTab(t.key)}
              style={{
                flex: 1, padding: "12px 4px", border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: rightTab === t.key ? "bold" : "normal",
                background: rightTab === t.key ? "#fff" : "#f0f2f5",
                borderBottom: rightTab === t.key ? "2px solid #25D366" : "2px solid transparent",
                color: rightTab === t.key ? "#166534" : "#555",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>

          {/* ── Tab: Chat ─────────────────────────────────────────────────── */}
          {rightTab === "chat" && (
            selectedChat ? (
              <>
                <p style={{ margin: "4px 0" }}><strong>Teléfono:</strong> {selectedChat.phone}</p>
                <div style={{ marginTop: "4px", fontWeight: "bold",
                              color: selectedChat.mode === "HUMAN" ? "#16a34a" : "#2563eb" }}>
                  {selectedChat.mode === "HUMAN" ? "🟢 HUMANO" : "🤖 BOT"}
                </div>
                <p style={{ margin: "4px 0" }}><strong>Status:</strong> {selectedChat.status}</p>
                <p style={{ margin: "4px 0" }}><strong>Agente:</strong> {selectedChat.assigned_agent || "-"}</p>

                <div style={{ marginTop: "16px", marginBottom: "20px" }}>
                  <button
                    onClick={() => takeChat(selectedChat.id)}
                    style={{ width: "100%", padding: "10px", marginBottom: "8px",
                             border: "none", borderRadius: "8px", cursor: "pointer",
                             background: "#dcfce7", color: "#166534", fontWeight: "bold" }}
                  >
                    👤 Tomar chat
                  </button>
                  <button
                    onClick={() => releaseChat(selectedChat.id)}
                    style={{ width: "100%", padding: "10px", border: "none",
                             borderRadius: "8px", cursor: "pointer",
                             background: "#eff6ff", color: "#1e40af", fontWeight: "bold" }}
                  >
                    🤖 Regresar al bot
                  </button>
                </div>

                <div style={{ borderTop: "1px solid #ddd", paddingTop: "16px" }}>
                  <h4 style={{ margin: "0 0 12px 0", color: "#374151" }}>Historial de pedidos</h4>
                  {!customerHistory ? (
                    <p style={{ color: "#9ca3af", fontSize: 13 }}>Cargando...</p>
                  ) : customerHistory.total_orders === 0 ? (
                    <p style={{ color: "#9ca3af", fontSize: 13 }}>Sin pedidos registrados</p>
                  ) : (
                    <>
                      {customerHistory.customer_name && (
                        <p style={{ margin: "0 0 4px 0", fontSize: 14 }}>
                          <strong>Nombre:</strong> {customerHistory.customer_name}
                        </p>
                      )}
                      <p style={{ margin: "0 0 12px 0", fontSize: 14 }}>
                        <strong>Total de pedidos:</strong>{" "}
                        <span style={{ background: "#f3f4f6", padding: "2px 8px",
                                       borderRadius: 12, fontWeight: "bold" }}>
                          {customerHistory.total_orders}
                        </span>
                      </p>
                      {customerHistory.active_order && (
                        <div style={{ background: "#fefce8", border: "1px solid #fde047",
                                      borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: "bold", color: "#854d0e", marginBottom: 6 }}>
                            PEDIDO ACTIVO — #{customerHistory.active_order.id}
                          </div>
                          <div style={{ fontSize: 13, color: "#713f12", marginBottom: 4 }}>
                            Estado: <strong>{customerHistory.active_order.status}</strong>
                          </div>
                          <div style={{ fontSize: 13, color: "#713f12" }}>
                            Total: <strong>${customerHistory.active_order.total_amount.toFixed(2)}</strong>
                          </div>
                        </div>
                      )}
                      {customerHistory.last_order && (
                        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
                                      borderRadius: 8, padding: "10px 12px" }}>
                          <div style={{ fontSize: 12, fontWeight: "bold", color: "#6b7280", marginBottom: 6 }}>
                            ÚLTIMO PEDIDO — #{customerHistory.last_order.id}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                            {new Date(customerHistory.last_order.created_at).toLocaleDateString("es-MX", {
                              day: "2-digit", month: "short", year: "numeric",
                              hour: "2-digit", minute: "2-digit",
                            })}
                          </div>
                          <div style={{ fontSize: 13, marginBottom: 4 }}>
                            Total: <strong>${customerHistory.last_order.total_amount.toFixed(2)}</strong>
                          </div>
                          {customerHistory.last_order.items?.length > 0 && (
                            <ul style={{ margin: "6px 0 0 0", paddingLeft: 16, fontSize: 12, color: "#4b5563" }}>
                              {customerHistory.last_order.items.slice(0, 4).map((item, i) => (
                                <li key={i}>
                                  {item.name || item.product_name || item.product || item.title || "Producto"}
                                  {item.quantity > 1 ? ` x${item.quantity}` : ""}
                                </li>
                              ))}
                              {customerHistory.last_order.items.length > 4 && (
                                <li style={{ color: "#9ca3af" }}>+{customerHistory.last_order.items.length - 4} más</li>
                              )}
                            </ul>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <p style={{ color: "#9ca3af" }}>Selecciona una conversación</p>
            )
          )}

          {/* ── Tab: Buscar cliente ────────────────────────────────────────── */}
          {rightTab === "search" && (
            <div>
              <p style={{ margin: "0 0 12px 0", fontSize: 13, color: "#555" }}>
                Busca el historial de un cliente por número de teléfono.
              </p>
              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                <input
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchCustomer()}
                  placeholder="52XXXXXXXXXX"
                  style={{ flex: 1, padding: "8px 10px", borderRadius: 8,
                           border: "1px solid #ccc", fontSize: 13, outline: "none" }}
                />
                <button
                  onClick={searchCustomer}
                  disabled={searchLoading}
                  style={{ padding: "8px 12px", border: "none", borderRadius: 8,
                           background: "#25D366", color: "white", cursor: "pointer",
                           fontWeight: "bold", fontSize: 13 }}
                >
                  {searchLoading ? "..." : "Buscar"}
                </button>
              </div>

              {searchResult && (
                !searchResult.found ? (
                  <div style={{ padding: "12px", background: "#fef2f2",
                                border: "1px solid #fecaca", borderRadius: 8,
                                fontSize: 13, color: "#b91c1c" }}>
                    No se encontraron pedidos para <strong>{searchResult.customer_phone}</strong>.
                  </div>
                ) : (
                  <div>
                    {/* Resumen */}
                    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0",
                                  borderRadius: 8, padding: "12px", marginBottom: 12 }}>
                      {searchResult.customer_name && (
                        <p style={{ margin: "0 0 4px 0", fontSize: 14, fontWeight: "bold" }}>
                          {searchResult.customer_name}
                        </p>
                      )}
                      <p style={{ margin: "0 0 2px 0", fontSize: 13, color: "#555" }}>
                        Tel: {searchResult.customer_phone}
                      </p>
                      <p style={{ margin: "0 0 2px 0", fontSize: 13 }}>
                        Pedidos: <strong>{searchResult.total_orders}</strong>
                      </p>
                      <p style={{ margin: 0, fontSize: 13 }}>
                        Gasto total: <strong>${searchResult.total_spent.toFixed(2)}</strong>
                      </p>
                    </div>

                    {/* Pedido activo */}
                    {searchResult.active_order && (
                      <div style={{ background: "#fefce8", border: "1px solid #fde047",
                                    borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: "bold", color: "#854d0e", marginBottom: 4 }}>
                          PEDIDO ACTIVO — #{searchResult.active_order.id}
                        </div>
                        <div style={{ fontSize: 13, color: "#713f12" }}>
                          {searchResult.active_order.status} · ${searchResult.active_order.total_amount.toFixed(2)}
                        </div>
                      </div>
                    )}

                    {/* Últimos pedidos */}
                    <h4 style={{ margin: "0 0 8px 0", fontSize: 13, color: "#374151" }}>
                      Últimos pedidos
                    </h4>
                    {searchResult.orders.slice(0, 5).map((order) => (
                      <div key={order.id}
                           style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
                                    borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between",
                                      fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                          <span>#{order.id}</span>
                          <span>{order.created_at
                            ? new Date(order.created_at).toLocaleDateString("es-MX", {
                                day: "2-digit", month: "short",
                              })
                            : ""}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between",
                                      fontSize: 13 }}>
                          <span style={{ color: "#374151" }}>{order.status}</span>
                          <strong>${order.total_amount.toFixed(2)}</strong>
                        </div>
                        {order.items?.length > 0 && (
                          <ul style={{ margin: "4px 0 0 0", paddingLeft: 14,
                                       fontSize: 11, color: "#6b7280" }}>
                            {order.items.slice(0, 3).map((item, i) => (
                              <li key={i}>
                                {item.name || item.product_name || item.product || item.title || "Producto"}
                              </li>
                            ))}
                            {order.items.length > 3 && (
                              <li>+{order.items.length - 3} más</li>
                            )}
                          </ul>
                        )}
                      </div>
                    ))}
                    {searchResult.orders.length > 5 && (
                      <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", margin: 0 }}>
                        Mostrando 5 de {searchResult.orders.length} pedidos
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          )}

        </div>
      </div>

    </div>
  );
}
